import { cn } from '@veem/ui'
import {
  createSignal,
  type JSX,
  onCleanup,
  onMount,
  type ParentProps,
  Show,
  splitProps,
} from 'solid-js'
import { stageByKey } from '../data/catalog.ts'
import { initials, personById } from '../data/people.ts'

const AVATAR_TONES = [
  'bg-stage-blue-soft text-stage-blue',
  'bg-stage-teal-soft text-stage-teal',
  'bg-stage-violet-soft text-stage-violet',
  'bg-stage-amber-soft text-stage-amber',
  'bg-stage-green-soft text-stage-green',
  'bg-stage-pink-soft text-stage-pink',
  'bg-stage-indigo-soft text-stage-indigo',
]

const toneFor = (seed: string) => {
  let hash = 0
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return AVATAR_TONES[hash % AVATAR_TONES.length]
}

/**
 * A colour per label. With `index` (the label's place in its list) neighbours never share a
 * colour until the palette runs out; without it the name decides, so it stays stable.
 */
export const labelTone = (label: string, index = -1) =>
  index >= 0
    ? AVATAR_TONES[index % AVATAR_TONES.length]
    : toneFor(label.trim().toLocaleLowerCase('uk-UA'))

export const Avatar = (props: {
  name: string
  size?: 'xs' | 'sm' | 'md' | 'lg'
  class?: string
}) => (
  <span
    aria-hidden="true"
    class={cn(
      'inline-grid shrink-0 place-items-center rounded-full font-semibold',
      props.size === 'xs' && 'size-5 text-[0.5625rem]',
      (props.size ?? 'sm') === 'sm' && 'size-6 text-micro',
      props.size === 'md' && 'size-8 text-caption',
      props.size === 'lg' && 'size-10 text-ui',
      toneFor(props.name),
      props.class,
    )}
  >
    {initials(props.name)}
  </span>
)

export const PersonChip = (props: { personId: string | null; empty?: string }) => (
  <Show
    when={props.personId}
    fallback={<span class="text-body text-text-subtle">{props.empty ?? 'Не призначено'}</span>}
  >
    {(id) => (
      <span class="inline-flex min-w-0 items-center gap-2 text-body text-text">
        <Avatar name={personById(id()).name} size="xs" />
        <span class="truncate">{personById(id()).name}</span>
      </span>
    )}
  </Show>
)

const STAGE_TONE: Record<string, string> = {
  slate: 'bg-stage-slate-soft text-text-muted [--dot:var(--color-stage-slate)]',
  blue: 'bg-stage-blue-soft text-stage-blue [--dot:var(--color-stage-blue)]',
  indigo: 'bg-stage-indigo-soft text-stage-indigo [--dot:var(--color-stage-indigo)]',
  violet: 'bg-stage-violet-soft text-stage-violet [--dot:var(--color-stage-violet)]',
  amber: 'bg-stage-amber-soft text-stage-amber [--dot:var(--color-stage-amber)]',
  green: 'bg-stage-green-soft text-stage-green [--dot:var(--color-stage-green)]',
  red: 'bg-stage-red-soft text-stage-red [--dot:var(--color-stage-red)]',
  pink: 'bg-stage-pink-soft text-stage-pink [--dot:var(--color-stage-pink)]',
  orange: 'bg-stage-orange-soft text-stage-orange [--dot:var(--color-stage-orange)]',
  teal: 'bg-stage-teal-soft text-stage-teal [--dot:var(--color-stage-teal)]',
}

/** Written out, not interpolated: Tailwind only ships classes it can find in the source. */
export const STAGE_DOT: Record<string, string> = {
  slate: 'bg-stage-slate',
  blue: 'bg-stage-blue',
  indigo: 'bg-stage-indigo',
  violet: 'bg-stage-violet',
  pink: 'bg-stage-pink',
  red: 'bg-stage-red',
  orange: 'bg-stage-orange',
  amber: 'bg-stage-amber',
  green: 'bg-stage-green',
  teal: 'bg-stage-teal',
}

/** A CRM status. Colour is the admin's label colour; the category carries the meaning. */
export const StageBadge = (props: { stageKey: string }) => {
  const stage = () => stageByKey(props.stageKey)
  return (
    <span
      class={cn(
        'inline-flex items-center gap-1.5 rounded-control px-1.5 py-0.5 text-caption font-semibold whitespace-nowrap',
        STAGE_TONE[stage().color],
      )}
    >
      <span class="size-1.5 rounded-full bg-(--dot)" aria-hidden="true" />
      {stage().label}
    </span>
  )
}

export type Tone = 'neutral' | 'active' | 'done' | 'warning' | 'critical' | 'draft'

const PILL_TONE: Record<Tone, string> = {
  neutral: 'bg-surface-sunken text-text-muted',
  active: 'bg-status-active-soft text-brand-800',
  done: 'bg-status-done-soft text-status-done',
  warning: 'bg-status-warning-soft text-status-warning',
  critical: 'bg-status-critical-soft text-status-critical',
  draft: 'bg-draft-soft text-draft',
}

export const Pill = (props: ParentProps<{ tone?: Tone; class?: string }>) => (
  <span
    class={cn(
      'inline-flex items-center gap-1 rounded-control px-1.5 py-0.5 text-caption font-semibold whitespace-nowrap',
      PILL_TONE[props.tone ?? 'neutral'],
      props.class,
    )}
  >
    {props.children}
  </span>
)

export const Progress = (props: {
  value: number
  tone?: 'active' | 'done' | 'warning'
  label: string
}) => (
  <span
    role="progressbar"
    aria-label={props.label}
    aria-valuenow={props.value}
    aria-valuemin={0}
    aria-valuemax={100}
    class="block h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
  >
    <span
      class={cn(
        'block h-full rounded-full',
        (props.tone ?? 'active') === 'active' && 'bg-accent',
        props.tone === 'done' && 'bg-status-done',
        props.tone === 'warning' && 'bg-status-warning',
      )}
      style={{ width: `${props.value}%` }}
    />
  </span>
)

export const IconButton = (
  props: JSX.ButtonHTMLAttributes<HTMLButtonElement> & { label: string },
) => {
  const [local, rest] = splitProps(props, ['label', 'class', 'children'])
  return (
    <button
      type="button"
      aria-label={local.label}
      title={local.label}
      {...rest}
      class={cn(
        'inline-grid size-8 shrink-0 place-items-center rounded-control text-text-subtle max-md:size-(--size-touch)',
        'transition-[background-color,color,transform] duration-(--duration-press) ease-out active:scale-[0.97]',
        'hover:bg-surface-sunken hover:text-text',
        'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent',
        '[&_svg]:size-4',
        local.class,
      )}
    >
      {local.children}
    </button>
  )
}

/** Closes on outside pointer and Escape. Enough for the prototype's menus and popovers. */
export const usePopover = () => {
  const [open, setOpen] = createSignal(false)
  let root: HTMLElement | undefined
  onMount(() => {
    const onPointer = (event: PointerEvent) => {
      if (open() && root && event.target instanceof Node && !root.contains(event.target))
        setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (open() && event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    onCleanup(() => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    })
  })
  return { open, setOpen, ref: (element: HTMLElement) => (root = element) }
}

export const PopoverPanel = (props: ParentProps<{ class?: string }>) => (
  <div
    class={cn(
      'absolute z-40 rounded-panel border border-border bg-surface p-1.5 shadow-overlay',
      'origin-top animate-[surface-in_var(--duration-fast)_var(--ease-out)]',
      props.class,
    )}
  >
    {props.children}
  </div>
)

export const MenuRow = (
  props: ParentProps<{
    onClick?: () => void
    icon?: JSX.Element
    trailing?: JSX.Element
    selected?: boolean
  }>,
) => (
  <button
    type="button"
    onClick={() => props.onClick?.()}
    class={cn(
      'flex w-full items-center gap-2.5 rounded-control px-2 py-1.5 text-left text-body text-text max-md:min-h-(--size-touch)',
      'hover:bg-surface-sunken focus-visible:bg-surface-sunken focus-visible:outline-none',
      props.selected && 'bg-surface-sunken',
      '[&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-text-subtle',
    )}
  >
    {props.icon}
    <span class="min-w-0 flex-1 truncate">{props.children}</span>
    {props.trailing}
  </button>
)

export const SectionLabel = (props: ParentProps<{ class?: string }>) => (
  <h3 class={cn('text-caption font-semibold text-text-subtle', props.class)}>{props.children}</h3>
)
